import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getFamille, createPaiementFamille, getMontantsMensuelsFamilles, getCotisationsFamilles, createCotisationFamille } from '../services/api';
import { notificationService } from '../utils/notifications';
import Footer from '../components/Footer';
import './ConfirmationPaiements.css';

const ConfirmationPaiementsFamilleFinances = () => {
  const navigate = useNavigate();
  const { familleId } = useParams();
  const finances = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('finances_user') || '{}');
    } catch {
      return {};
    }
  }, []);

  const [famille, setFamille] = useState(null);
  const [montantsMensuels, setMontantsMensuels] = useState([]);
  const [cotisationsParMois, setCotisationsParMois] = useState({});
  const [loadingCotisationsParMois, setLoadingCotisationsParMois] = useState({});
  const [moisExpande, setMoisExpande] = useState({});
  const [loading, setLoading] = useState(true);
  const [tousLesMois, setTousLesMois] = useState([]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      console.log('📊 Chargement des données pour la famille:', familleId);
      
      const [familleRes, montantsRes, cotisationsRes] = await Promise.allSettled([
        getFamille(parseInt(familleId)),
        getMontantsMensuelsFamilles(),
        getCotisationsFamilles({ famille_id: parseInt(familleId) })
      ]);
      
      // Traiter la réponse de la famille
      let familleData = null;
      if (familleRes.status === 'fulfilled') {
        familleData = familleRes.value.data;
      } else {
        console.error('❌ Erreur lors du chargement de la famille:', familleRes.reason);
        notificationService.error('Erreur', 'Impossible de charger les informations de la famille');
        navigate('/finances');
        return;
      }
      
      if (!familleData) {
        console.error('❌ Famille non trouvée:', familleId);
        notificationService.error('Erreur', 'Famille non trouvée');
        navigate('/finances');
        return;
      }
      
      setFamille(familleData);
      
      // Traiter la réponse des montants mensuels
      let montants = [];
      if (montantsRes.status === 'fulfilled') {
        montants = montantsRes.value.data || [];
      } else {
        console.warn('⚠️ Erreur lors du chargement des montants mensuels:', montantsRes.reason);
      }
      setMontantsMensuels(montants);
      
      // Traiter la réponse des cotisations
      let cotisations = [];
      if (cotisationsRes.status === 'fulfilled') {
        // L'API peut retourner directement un tableau ou un objet avec data
        const responseData = cotisationsRes.value.data;
        cotisations = Array.isArray(responseData) ? responseData : (responseData?.data || []);
        console.log('📋 Cotisations trouvées:', cotisations.length);
      } else {
        console.warn('⚠️ Erreur lors du chargement des cotisations:', cotisationsRes.reason);
        console.warn('⚠️ Détails:', cotisationsRes.reason?.response?.data || cotisationsRes.reason?.message);
        // Continuer même si les cotisations ne peuvent pas être chargées
        cotisations = [];
      }
      
      const moisUniques = new Map();
      
      // D'abord, ajouter tous les mois qui ont des cotisations
      cotisations.forEach(cotisation => {
        const key = `${cotisation.annee}-${cotisation.mois}`;
        if (!moisUniques.has(key)) {
          moisUniques.set(key, {
            annee: cotisation.annee,
            mois: cotisation.mois,
            cotisations: []
          });
        }
        moisUniques.get(key).cotisations.push(cotisation);
      });
      
      // Ensuite, ajouter TOUS les mois qui ont un montant mensuel fixé
      montants.forEach(montant => {
        const key = `${montant.annee}-${montant.mois}`;
        if (!moisUniques.has(key)) {
          moisUniques.set(key, {
            annee: montant.annee,
            mois: montant.mois,
            cotisations: []
          });
        }
      });
      
      // Trier (plus récent en premier)
      let moisList = Array.from(moisUniques.values());
      moisList.sort((a, b) => {
        if (a.annee !== b.annee) return b.annee - a.annee;
        return b.mois - a.mois;
      });
      
      console.log('📅 Mois uniques trouvés:', moisList.length);
      setTousLesMois(moisList);
      
      // Grouper les cotisations par mois pour l'affichage
      const cotisationsGrouped = {};
      cotisations.forEach(cotisation => {
        const key = `${cotisation.annee}-${cotisation.mois}`;
        if (!cotisationsGrouped[key]) {
          cotisationsGrouped[key] = [];
        }
        cotisationsGrouped[key].push(cotisation);
      });
      setCotisationsParMois(cotisationsGrouped);
      
      console.log('✅ Données chargées avec succès');
    } catch (err) {
      console.error('❌ Erreur lors du chargement des données:', err);
      console.error('❌ Détails:', err.response?.data || err.message);
      notificationService.error('Erreur', 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [familleId, navigate]);

  useEffect(() => {
    if (!finances.id) {
      navigate('/login-finances');
      return;
    }
    if (finances.role !== 'finances') {
      navigate('/login-finances');
      return;
    }
    
    // Réinitialiser les états liés aux cotisations quand la famille change
    setCotisationsParMois({});
    setMoisExpande({});
    setLoadingCotisationsParMois({});
    setTousLesMois([]);
    
    loadData();
  }, [finances.id, finances.role, navigate, familleId, loadData]);

  const getMoisNom = (mois) => {
    const moisNoms = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return moisNoms[mois - 1] || mois;
  };

  const loadCotisationsImpayeesParMois = async (mois, annee) => {
    if (!familleId) return;
    
    const key = `${annee}-${mois}`;
    setLoadingCotisationsParMois(prev => ({ ...prev, [key]: true }));
    try {
      const response = await getCotisationsFamilles({ 
        famille_id: parseInt(familleId),
        mois: mois,
        annee: annee
      });
      // Filtrer uniquement les cotisations impayées
      const cotisations = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      const cotisationsImpayees = cotisations.filter(c => c.statut === 'impaye');
      setCotisationsParMois(prev => ({ ...prev, [key]: cotisationsImpayees }));
    } catch (err) {
      console.error(`Erreur lors du chargement des cotisations pour ${mois}/${annee}:`, err);
      setCotisationsParMois(prev => ({ ...prev, [key]: [] }));
    } finally {
      setLoadingCotisationsParMois(prev => ({ ...prev, [key]: false }));
    }
  };

  const toggleMoisExpande = (mois, annee) => {
    const key = `${annee}-${mois}`;
    const isExpanded = moisExpande[key] || false;
    
    if (!isExpanded) {
      if (!cotisationsParMois[key]) {
        loadCotisationsImpayeesParMois(mois, annee);
      }
    }
    
    setMoisExpande(prev => ({ ...prev, [key]: !isExpanded }));
  };

  const handleConfirmerPaiement = async (mois, annee) => {
    const familleIdNum = parseInt(familleId);
    if (!familleIdNum) {
      notificationService.error('Erreur', 'Impossible d\'identifier la famille.');
      return;
    }

    // Trouver le montant mensuel pour ce mois
    const montantMensuel = montantsMensuels.find(m => m.mois === mois && m.annee === annee);
    if (!montantMensuel) {
      notificationService.error('Erreur', 'Aucun montant mensuel trouvé pour ce mois.');
      return;
    }

    const montant = parseFloat(montantMensuel.montant_mensuel || 0);
    if (!montant) {
      notificationService.error('Erreur', 'Aucun montant défini pour ce mois');
      return;
    }

    // Vérifier si une cotisation existe déjà pour ce mois
    const cotisationsExistantes = cotisationsParMois[`${annee}-${mois}`] || [];
    const cotisationExistante = cotisationsExistantes.find(c => c.statut === 'impaye');
    
    if (cotisationExistante && cotisationExistante.statut === 'paye') {
      notificationService.warning('Déjà payée', 'Cette cotisation est déjà payée.');
      return;
    }

    const familleNom = famille ? famille.nom_famille : 'Famille';
    const messageConfirmation = `Confirmer le paiement de ${montant.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF pour ${familleNom} - ${getMoisNom(mois)} ${annee} ?`;

    if (!window.confirm(messageConfirmation)) {
      return;
    }

    try {
      let cotisationId = cotisationExistante?.id;
      
      // Si la cotisation n'existe pas encore (pas d'ID), la créer d'abord
      if (!cotisationId) {
        // Calculer la date d'échéance (fin du mois)
        const dateEcheance = new Date(annee, mois, 0); // Dernier jour du mois
        
        try {
          // Créer la cotisation mensuelle (sans semaine)
          const cotisationResponse = await createCotisationFamille({
            famille_id: familleIdNum,
            mois: mois,
            annee: annee,
            montant: montant,
            date_echeance: dateEcheance.toISOString().split('T')[0]
          });
          
          console.log('✅ Réponse création cotisation:', cotisationResponse.data);
          
          // Vérifier la structure de la réponse
          if (cotisationResponse.data && cotisationResponse.data.cotisation) {
            cotisationId = cotisationResponse.data.cotisation.id;
          } else if (cotisationResponse.data && cotisationResponse.data.id) {
            cotisationId = cotisationResponse.data.id;
          } else {
            throw new Error('Structure de réponse inattendue lors de la création de la cotisation');
          }
        } catch (cotisationError) {
          console.error('❌ Erreur lors de la création de la cotisation:', cotisationError);
          const cotisationErrorMessage = cotisationError.response?.data?.message || cotisationError.message || 'Erreur lors de la création de la cotisation';
          notificationService.error('Erreur', cotisationErrorMessage);
          return;
        }
      }

      console.log('💰 Création du paiement pour cotisation ID:', cotisationId);
      
      const paiementData = {
        famille_id: familleIdNum,
        montant: montant,
        date_paiement: new Date().toISOString().split('T')[0],
        type_paiement: 'cotisation_famille',
        cotisation_ids: [cotisationId],
        confirmed_by: 'finances'
      };

      // Mode en ligne : enregistrer normalement
      try {
        const paiementResponse = await createPaiementFamille(paiementData);

        console.log('✅ Paiement créé avec succès:', paiementResponse.data);
        notificationService.success('Paiement confirmé', `Le paiement de ${familleNom} a été confirmé avec succès.`);
        
        // Recharger les cotisations pour ce mois
        await loadCotisationsImpayeesParMois(mois, annee);
        // Recharger les données de la famille
        await loadData();
      } catch (paiementError) {
        console.error('❌ Erreur lors de la création du paiement:', paiementError);
        const paiementErrorMessage = paiementError.response?.data?.message || paiementError.response?.data?.error || paiementError.message || 'Erreur lors de la création du paiement';
        notificationService.error('Erreur', paiementErrorMessage);
      }
    } catch (err) {
      console.error('❌ Erreur générale lors de la confirmation du paiement:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Erreur lors de la confirmation du paiement';
      notificationService.error('Erreur', errorMessage);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Chargement...</p>
      </div>
    );
  }

  if (!famille) {
    return (
      <div className="container">
        <div className="card">
          <h2>Famille non trouvée</h2>
          <button className="btn btn-secondary" onClick={() => navigate('/finances')}>
            Retour à la liste
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <div className="header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img src="/logo.png" alt="Logo" style={{ height: '50px', width: '50px', objectFit: 'contain' }} />
            <div>
              <h1>MUTU NDE ESPOIR ASBL</h1>
              <p>Confirmation des paiements - Famille - Finances</p>
              <p style={{ fontSize: '14px', color: '#666' }}>
                {famille.nom_famille} - Numéro: {famille.numero_famille}
              </p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={() => navigate('/finances')}>
              ← Retour à la liste
            </button>
            <button className="btn btn-danger" onClick={() => {
              localStorage.removeItem('finances_token');
              localStorage.removeItem('finances_user');
              navigate('/login-finances');
            }}>
              Déconnexion
            </button>
          </div>
        </div>
      </div>

      <div className="container">
        {/* Carte d'information de la famille */}
        <div className="card" style={{ 
          marginBottom: '30px', 
          backgroundColor: '#e3f2fd', 
          border: '2px solid #2196f3',
          borderRadius: '10px',
          padding: '20px'
        }}>
          <h2 style={{ 
            marginBottom: '15px', 
            color: '#1565c0',
            fontSize: '22px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            👨‍👩‍👧‍👦 Informations de la famille
          </h2>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
            gap: '15px' 
          }}>
            <div style={{
              padding: '15px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '1px solid #90caf9'
            }}>
              <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>NOM DE LA FAMILLE</div>
              <div style={{ fontSize: '18px', color: '#1565c0', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {famille.nom_famille || '-'}
              </div>
            </div>
            <div style={{
              padding: '15px',
              backgroundColor: '#fff',
              borderRadius: '8px',
              border: '1px solid #90caf9'
            }}>
              <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>NUMÉRO FAMILLE</div>
              <div style={{ fontSize: '18px', color: '#1565c0', fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                {famille.numero_famille || '-'}
              </div>
            </div>
            {famille.chef_famille_nom && (
              <div style={{
                padding: '15px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #90caf9'
              }}>
                <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>CHEF DE FAMILLE</div>
                <div style={{ fontSize: '16px', color: '#1565c0', whiteSpace: 'nowrap' }}>
                  {famille.chef_famille_nom}
                </div>
              </div>
            )}
            {famille.commune && (
              <div style={{
                padding: '15px',
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: '1px solid #90caf9'
              }}>
                <div style={{ fontSize: '12px', color: '#1565c0', marginBottom: '5px', fontWeight: 'bold' }}>COMMUNE</div>
                <div style={{ fontSize: '16px', color: '#1565c0', whiteSpace: 'nowrap' }}>
                  {famille.commune}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="card" style={{ marginBottom: '30px' }}>
          <h2 style={{ marginBottom: '20px' }}>📋 Confirmation des paiements par mois</h2>
          <p style={{ color: '#666', marginBottom: '15px', fontSize: '14px' }}>
            Confirmez les paiements mensuels pour chaque mois.
          </p>
          
          {/* Légende des couleurs */}
          <div style={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '15px', 
            marginBottom: '20px',
            padding: '15px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            border: '1px solid #e0e0e0'
          }}>
            <span style={{ fontWeight: 'bold', color: '#333', marginRight: '10px' }}>Légende :</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#d4edda', 
                border: '2px solid #28a745',
                borderRadius: '4px' 
              }}></div>
              <span style={{ fontSize: '13px', color: '#155724' }}>Mois entièrement payé</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#fff3cd', 
                border: '2px solid #ffc107',
                borderRadius: '4px' 
              }}></div>
              <span style={{ fontSize: '13px', color: '#856404' }}>Cotisation impayée</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: '#f8d7da', 
                border: '2px solid #dc3545',
                borderRadius: '4px' 
              }}></div>
              <span style={{ fontSize: '13px', color: '#721c24' }}>Mois non cotisé</span>
            </div>
          </div>
          
          {tousLesMois.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <p style={{ fontSize: '16px', marginBottom: '10px' }}>Aucune cotisation trouvée</p>
              <p style={{ fontSize: '14px' }}>Cette famille n'a pas encore de cotisations enregistrées.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {tousLesMois.map((moisData) => {
                  const key = `${moisData.annee}-${moisData.mois}`;
                  const isExpanded = moisExpande[key] || false;
                  
                  // Obtenir les cotisations pour ce mois
                  let cotisationsFamille = cotisationsParMois[key] || [];
                  // Si pas encore chargées, utiliser les cotisations du moisData
                  if (cotisationsFamille.length === 0 && moisData.cotisations) {
                    cotisationsFamille = moisData.cotisations;
                  }
                  
                  // Filtrer uniquement les cotisations qui ont un ID (qui existent vraiment en base)
                  const cotisationsReelles = cotisationsFamille.filter(c => {
                    const hasId = c.id != null && c.id !== undefined && c.id !== '';
                    return hasId;
                  });
                  
                  const totalImpaye = cotisationsReelles.filter(c => c.statut === 'impaye').reduce((sum, c) => sum + parseFloat(c.montant || 0), 0);
                  
                  // Trouver le montant mensuel fixé pour ce mois (si disponible)
                  const montantMensuel = montantsMensuels.find(m => m.mois === moisData.mois && m.annee === moisData.annee);
                  
                  // Vérifier si c'est un mois futur
                  const dateActuelle = new Date();
                  const anneeActuelle = dateActuelle.getFullYear();
                  const moisActuel = dateActuelle.getMonth() + 1;
                  const estMoisFutur = moisData.annee > anneeActuelle || 
                                      (moisData.annee === anneeActuelle && moisData.mois > moisActuel);
                  
                  // Vérifier le statut des cotisations
                  const cotisationsImpayees = cotisationsReelles.filter(c => c.statut === 'impaye');
                  const cotisationsPayees = cotisationsReelles.filter(c => c.statut === 'paye');
                  const cotisationExiste = cotisationsReelles.length > 0;
                  const aUneCotisationPayee = cotisationsPayees.length > 0 && cotisationExiste;
                  const aUneCotisationImpayee = cotisationsImpayees.length > 0 && cotisationExiste;
                  
                  // Déterminer le style selon le statut
                  const getStatusStyle = () => {
                    if (aUneCotisationPayee && !aUneCotisationImpayee) {
                      // Mois entièrement payé - VERT
                      return {
                        backgroundColor: '#d4edda',
                        borderColor: '#28a745',
                        hoverBg: '#c3e6cb',
                        icon: '✅',
                        statusText: 'Payé'
                      };
                    } else if (aUneCotisationImpayee) {
                      // Mois avec cotisation impayée - JAUNE/ORANGE
                      return {
                        backgroundColor: '#fff3cd',
                        borderColor: '#ffc107',
                        hoverBg: '#ffe69c',
                        icon: '⚠️',
                        statusText: 'Impayé'
                      };
                    } else {
                      // Mois non cotisé (pas de cotisations créées) - ROUGE CLAIR
                      return {
                        backgroundColor: '#f8d7da',
                        borderColor: '#dc3545',
                        hoverBg: '#f5c6cb',
                        icon: '❌',
                        statusText: 'Non cotisé'
                      };
                    }
                  };
                  
                  const statusStyle = getStatusStyle();
                  
                  return (
                    <div
                      key={key}
                      style={{
                        border: `2px solid ${statusStyle.borderColor}`,
                        borderRadius: '8px',
                        overflow: 'hidden',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div
                        style={{
                          padding: '15px',
                          backgroundColor: statusStyle.backgroundColor,
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'background-color 0.2s'
                        }}
                        onClick={() => toggleMoisExpande(moisData.mois, moisData.annee)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = statusStyle.hoverBg}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = statusStyle.backgroundColor}
                      >
                        <div style={{ flex: 1 }}>
                          <h3 style={{ margin: 0, fontSize: '18px', color: '#2c3e50', display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{ fontSize: '20px' }}>{statusStyle.icon}</span>
                            {getMoisNom(moisData.mois)} {moisData.annee}
                            <span style={{ 
                              fontSize: '11px', 
                              padding: '4px 10px', 
                              backgroundColor: statusStyle.borderColor,
                              color: 'white', 
                              borderRadius: '12px',
                              fontWeight: 'bold',
                              textTransform: 'uppercase'
                            }}>
                              {statusStyle.statusText}
                            </span>
                            {estMoisFutur && (
                              <span style={{ 
                                fontSize: '12px', 
                                padding: '4px 8px', 
                                backgroundColor: '#e3f2fd', 
                                color: '#1976d2', 
                                borderRadius: '4px',
                                fontWeight: 'normal'
                              }}>
                                📅 Mois futur
                              </span>
                            )}
                          </h3>
                          {montantMensuel ? (
                            <div style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                              Montant mensuel: <strong>{parseFloat(montantMensuel.montant_mensuel).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF</strong>
                            </div>
                          ) : (
                            <div style={{ fontSize: '14px', color: '#999', marginTop: '5px', fontStyle: 'italic' }}>
                              Montant mensuel non fixé par l'administrateur
                            </div>
                          )}
                          {isExpanded && (
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '8px' }}>
                              {aUneCotisationImpayee ? (
                                <span style={{ color: '#856404', fontWeight: 'bold' }}>
                                  Cotisation impayée • Montant: {totalImpaye.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                                </span>
                              ) : aUneCotisationPayee ? (
                                <span style={{ color: '#155724', fontWeight: 'bold' }}>
                                  ✓ Cotisation payée
                                </span>
                              ) : (
                                <span style={{ color: '#666', fontStyle: 'italic' }}>
                                  Aucune cotisation pour ce mois
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div style={{ fontSize: '20px', color: '#666' }}>
                          {isExpanded ? '▼' : '▶'}
                        </div>
                      </div>
                      
                      {isExpanded && (
                        <div style={{ padding: '15px', backgroundColor: '#f8f9fa' }}>
                          {loadingCotisationsParMois[key] ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                              Chargement...
                            </div>
                          ) : (() => {
                            // Obtenir toutes les cotisations du mois
                            const toutesCotisations = moisData.cotisations || [];
                            // Chercher dans toutesCotisations ou dans cotisationsReelles
                            let cotisationMois = toutesCotisations.find(c => {
                              const hasId = c.id != null && c.id !== undefined && c.id !== '';
                              return hasId && c.mois === moisData.mois && c.annee === moisData.annee;
                            });
                            if (!cotisationMois) {
                              // Utiliser cotisationsReelles
                              cotisationMois = cotisationsReelles.find(c => c.mois === moisData.mois && c.annee === moisData.annee);
                            }
                            
                            // Vérifier si la cotisation est payée
                            const hasValidId = cotisationMois && cotisationMois.id != null && cotisationMois.id !== undefined && cotisationMois.id !== '';
                            const hasPaiementId = cotisationMois && cotisationMois.paiement_id != null && cotisationMois.paiement_id !== undefined;
                            const estPayee = hasValidId && hasPaiementId && cotisationMois.statut === 'paye';
                            
                            if (!cotisationMois || !hasValidId) {
                              // Pas de cotisation créée pour ce mois
                              if (montantMensuel) {
                                return (
                                  <div>
                                    <div style={{ textAlign: 'center', padding: '15px', color: '#856404', backgroundColor: '#fff3cd', borderRadius: '5px', marginBottom: '15px' }}>
                                      <strong>⚠️ La cotisation pour ce mois n'a pas encore été créée.</strong>
                                      <div style={{ fontSize: '13px', marginTop: '5px' }}>
                                        Vous pouvez confirmer le paiement directement. La cotisation sera créée automatiquement.
                                      </div>
                                    </div>
                                    <div style={{ textAlign: 'center' }}>
                                      <button
                                        className="btn btn-success"
                                        onClick={() => handleConfirmerPaiement(moisData.mois, moisData.annee)}
                                        style={{ fontSize: '14px', padding: '10px 20px' }}
                                      >
                                        ✓ Confirmer le paiement pour {getMoisNom(moisData.mois)} {moisData.annee}
                                      </button>
                                    </div>
                                  </div>
                                );
                              } else {
                                return (
                                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                    <strong>Aucune cotisation pour ce mois</strong>
                                    <div style={{ fontSize: '13px', marginTop: '10px', color: '#999' }}>
                                      L'administrateur n'a pas encore fixé de montant mensuel pour ce mois.
                                    </div>
                                  </div>
                                );
                              }
                            }
                            
                            return (
                              <div>
                                {estPayee ? (
                                  <div style={{ textAlign: 'center', padding: '20px', color: '#28a745', marginBottom: '15px' }}>
                                    <strong>✓ La cotisation de ce mois est payée!</strong>
                                    {cotisationMois.date_paiement && (
                                      <div style={{ fontSize: '13px', marginTop: '5px' }}>
                                        Payée le {new Date(cotisationMois.date_paiement).toLocaleDateString('fr-FR')}
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <div style={{ textAlign: 'center' }}>
                                    <div style={{ marginBottom: '15px' }}>
                                      <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                                        <strong>Montant:</strong> {parseFloat(cotisationMois.montant || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} CDF
                                      </p>
                                      {cotisationMois.date_echeance && (
                                        <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                                          <strong>Échéance:</strong> {new Date(cotisationMois.date_echeance).toLocaleDateString('fr-FR')}
                                        </p>
                                      )}
                                    </div>
                                    <button
                                      className="btn btn-success"
                                      onClick={() => handleConfirmerPaiement(moisData.mois, moisData.annee)}
                                      style={{ fontSize: '14px', padding: '10px 20px' }}
                                    >
                                      ✓ Confirmer le paiement
                                    </button>
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ConfirmationPaiementsFamilleFinances;
